import React, { useEffect, useRef } from "react";
import { Send } from "lucide-react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { MicIcon } from "../../../../Chat/components/WelcomeSections/InputIcons";
import { Textarea } from "components/ui/textarea";
import Icon from "../../../../YourAIAgents/components/components/icon";
import AnimatedText from "../../AnimatedText";
import LoadingDots from "../../LoadingDots";
import ModalCloseButton from "./ModalCloseButton";

const PromptIntroStep = ({
  pageMode = false,
  attachSlot = null,
  step,
  promptQuery,
  promptQueryError,
  onPromptQueryChange,
  onSubmit,
  onKeyDown,
  onClose,
}) => {
  // Voice input (page mode): same react-speech-recognition flow as the New
  // Task composer (ChatInput) — the transcript diff is appended to the query.
  const { transcript, listening, browserSupportsSpeechRecognition } =
    useSpeechRecognition();
  const processedLen = useRef(0);
  useEffect(() => {
    if (!pageMode) return;
    if (transcript.length > processedLen.current) {
      const diff = transcript.slice(processedLen.current);
      processedLen.current = transcript.length;
      onPromptQueryChange((promptQuery || "") + diff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  if (step === 1) {
    return (
      <div className="formGroup">
        <p className="roundofaddprompt z-w-100 text-center" style={{ marginBottom: 0 }}>
          <Icon id="magicstar" /> Creating task
          <LoadingDots speed={100} />
        </p>
      </div>
    );
  }

  if (step === 5) {
    return (
      <div className="formGroup">
        <ModalCloseButton onClick={onClose} />
        <p
          className="roundofaddprompt z-w-100 text-center bg-destructive/10 text-destructive"
          style={{ marginBottom: 0 }}
        >
          <Icon id="magicstar" /> Something Went Wrong!
        </p>
      </div>
    );
  }

  const isActive = (promptQuery || "").trim().length > 0;

  return (
    <div className="formGroup">
      {/* Page mode (Automate Tasks, CU-86carc99u): the page header carries the
          "What should we automate?" line, so the intro line + close X are
          modal-only. The composer grows to the Lovable card treatment. */}
      {!pageMode && <ModalCloseButton onClick={onClose} />}
      {!pageMode && (
        <p className="roundofaddprompt w-full">
          <Icon id="magicstar" />{" "}
          <AnimatedText text="What do you want to do?" speed={50} />
        </p>
      )}
      {/* CU-86cb3nnyu: page-mode card matches the New Task composer — neutral
          border at rest, task-accent border + soft ring while the caret is in
          the box, toolbar in its own divided band. */}
      <div
        className={
          pageMode
            ? "rounded-2xl border border-border bg-card transition-[border-color,box-shadow] duration-150 focus-within:border-[hsl(var(--task-accent)/0.45)] focus-within:shadow-[0_0_0_3px_hsl(var(--task-accent)/0.18)]"
            : "relative"
        }
      >
        <Textarea
          value={promptQuery}
          required
          placeholder={
            pageMode
              ? "Find any Xero invoices overdue by more than 14 days."
              : "Start Typing ..."
          }
          name="promptQuery"
          onChange={(event) => onPromptQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          className={
            pageMode
              ? // `!` beats the legacy `.formGroup textarea` rule (PromptModal/index.css)
                // that re-adds a border, 15px radius, padding and margin — that leak is
                // what rendered the box-in-a-box Danni screenshotted on CU-86cb3nnyu.
                "w-full resize-none !border-0 !rounded-none !m-0 bg-transparent shadow-none min-h-[110px] max-h-[240px] !px-4 !pt-4 !pb-2 text-lg font-light leading-relaxed placeholder:text-foreground/25 focus-visible:ring-0"
              : "v3chat__content w-full resize-none min-h-0 max-h-[60px] pr-12"
          }
          onInput={(event) => {
            event.target.style.height = "auto";
            event.target.style.height = `${event.target.scrollHeight}px`;
          }}
        />
        {pageMode ? (
          <div className="flex items-center justify-between border-t border-border/60 bg-muted/40 px-4 py-3 rounded-b-2xl">
            <div className="flex items-center gap-2">{attachSlot}</div>
            <div className="flex items-center gap-2">
              {browserSupportsSpeechRecognition && (
                <button
                  type="button"
                  aria-label="Voice input"
                  onClick={() =>
                    listening
                      ? SpeechRecognition.stopListening()
                      : SpeechRecognition.startListening({ continuous: true })
                  }
                  className={`outline-none w-8 h-8 inline-flex items-center justify-center ${
                    listening
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <MicIcon size={20} />
                </button>
              )}
              <button
                type="button"
                onClick={onSubmit}
                disabled={!isActive}
                aria-label="Send"
                className={`w-8 h-8 inline-flex items-center justify-center rounded-full p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  isActive
                    ? "bg-foreground text-background cursor-pointer"
                    : "bg-muted text-muted-foreground cursor-not-allowed opacity-70"
                }`}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onSubmit}
            disabled={!isActive}
            aria-label="Send"
            className={`absolute right-2.5 top-2.5 w-8 h-8 inline-flex items-center justify-center rounded-full p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              isActive
                ? "bg-foreground text-background cursor-pointer"
                : "bg-muted text-muted-foreground cursor-not-allowed opacity-70"
            }`}
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {promptQueryError && (
        <small className="text-destructive">Should not be empty</small>
      )}
    </div>
  );
};

export default PromptIntroStep;
